import type { components } from "../api/generated/schema";

const DATABASE_NAME = "sped-scheduler-prechecks";
const DATABASE_VERSION = 1;
const CANDIDATE_STORE = "precheck_candidates";

export type SchedulingCandidate = components["schemas"]["SchedulingCandidate"];
export type SchedulingAlternativeHistory =
  components["schemas"]["SchedulingAlternativeHistory"];

export interface SchedulingPrecheckContextV1 {
  relaxCourseAdjacency: false;
  searchMode: "STRICT";
  alternativeHistory: SchedulingAlternativeHistory | null;
}

export interface SchedulingPrecheckRecordV1 {
  schemaVersion: 1;
  projectId: string;
  sourceJobId: string;
  conditionRevision: number;
  timetableRevision: number;
  inputFingerprint: string;
  requestContext: SchedulingPrecheckContextV1;
  savedAt: string;
  candidate: SchedulingCandidate;
}

export class SchedulingPrecheckStorageError extends Error {
  readonly code: string;
  readonly causeValue: unknown;

  constructor(code: string, message: string, causeValue?: unknown) {
    super(message);
    this.name = "SchedulingPrecheckStorageError";
    this.code = code;
    this.causeValue = causeValue;
  }
}

export interface SchedulingPrecheckStore {
  get(projectId: string): Promise<SchedulingPrecheckRecordV1 | null>;
  put(record: SchedulingPrecheckRecordV1): Promise<void>;
  delete(projectId: string): Promise<void>;
  close(): void;
}

export async function openSchedulingPrecheckStore(): Promise<SchedulingPrecheckStore> {
  if (!("indexedDB" in globalThis)) {
    throw new SchedulingPrecheckStorageError(
      "PRECHECK_STORAGE_UNAVAILABLE",
      "当前浏览器不支持保存预检结果，请使用最新版 Chrome 或 Edge"
    );
  }
  try {
    return new IndexedDbSchedulingPrecheckStore(await openDatabase());
  } catch (error) {
    throw new SchedulingPrecheckStorageError(
      "PRECHECK_STORAGE_OPEN_FAILED",
      "无法打开预检结果存储，尚未开放生成课表",
      error
    );
  }
}

class IndexedDbSchedulingPrecheckStore implements SchedulingPrecheckStore {
  constructor(private readonly database: IDBDatabase) {
    database.addEventListener("versionchange", () => database.close());
  }

  async get(projectId: string): Promise<SchedulingPrecheckRecordV1 | null> {
    const transaction = this.database.transaction(CANDIDATE_STORE, "readonly");
    try {
      const value = await requestResult(
        transaction.objectStore(CANDIDATE_STORE).get(projectId)
      );
      await transactionDone(transaction);
      if (value === undefined) return null;
      const record = assertSchedulingPrecheckRecord(value);
      return structuredClone(record);
    } catch (error) {
      throw new SchedulingPrecheckStorageError(
        "PRECHECK_STORAGE_READ_FAILED",
        "无法读取已保存的预检结果，请重新预检",
        error
      );
    }
  }

  async put(record: SchedulingPrecheckRecordV1): Promise<void> {
    const normalized = assertSchedulingPrecheckRecord(record);
    const transaction = this.database.transaction(CANDIDATE_STORE, "readwrite");
    try {
      transaction.objectStore(CANDIDATE_STORE).put(structuredClone(normalized));
      await transactionDone(transaction);
    } catch (error) {
      abortQuietly(transaction);
      throw new SchedulingPrecheckStorageError(
        "PRECHECK_STORAGE_WRITE_FAILED",
        "预检候选未能保存到当前浏览器，因此尚未开放生成课表",
        error
      );
    }
  }

  async delete(projectId: string): Promise<void> {
    const transaction = this.database.transaction(CANDIDATE_STORE, "readwrite");
    try {
      transaction.objectStore(CANDIDATE_STORE).delete(projectId);
      await transactionDone(transaction);
    } catch (error) {
      abortQuietly(transaction);
      throw new SchedulingPrecheckStorageError(
        "PRECHECK_STORAGE_WRITE_FAILED",
        "旧预检结果未能清理，请刷新后重试",
        error
      );
    }
  }

  close(): void {
    this.database.close();
  }
}

export function assertSchedulingPrecheckRecord(
  value: unknown
): SchedulingPrecheckRecordV1 {
  if (!isRecord(value)) throw new Error("invalid precheck record");
  const context = value.requestContext;
  const candidate = value.candidate;
  if (
    value.schemaVersion !== 1 ||
    typeof value.projectId !== "string" ||
    typeof value.sourceJobId !== "string" ||
    !Number.isInteger(value.conditionRevision) ||
    Number(value.conditionRevision) < 0 ||
    !Number.isInteger(value.timetableRevision) ||
    Number(value.timetableRevision) < 0 ||
    typeof value.inputFingerprint !== "string" ||
    !/^[0-9a-f]{64}$/i.test(value.inputFingerprint) ||
    typeof value.savedAt !== "string" ||
    Number.isNaN(Date.parse(value.savedAt)) ||
    !isRecord(context) ||
    context.relaxCourseAdjacency !== false ||
    context.searchMode !== "STRICT" ||
    (context.alternativeHistory !== null && !isRecord(context.alternativeHistory)) ||
    !isRecord(candidate) ||
    candidate.project_id !== value.projectId ||
    candidate.condition_revision !== value.conditionRevision ||
    candidate.timetable_revision !== value.timetableRevision ||
    candidate.input_fingerprint !== value.inputFingerprint ||
    !Array.isArray(candidate.assignments) ||
    !Array.isArray(candidate.plan_decisions)
  ) {
    throw new Error("invalid precheck record");
  }
  return value as unknown as SchedulingPrecheckRecordV1;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.addEventListener("upgradeneeded", () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(CANDIDATE_STORE)) {
        database.createObjectStore(CANDIDATE_STORE, { keyPath: "projectId" });
      }
    });
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("IndexedDB open failed")),
      { once: true }
    );
    request.addEventListener(
      "blocked",
      () => reject(new Error("IndexedDB upgrade blocked")),
      { once: true }
    );
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("IndexedDB request failed")),
      { once: true }
    );
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener(
      "abort",
      () => reject(transaction.error ?? new Error("IndexedDB transaction aborted")),
      { once: true }
    );
    transaction.addEventListener(
      "error",
      () => reject(transaction.error ?? new Error("IndexedDB transaction failed")),
      { once: true }
    );
  });
}

function abortQuietly(transaction: IDBTransaction): void {
  try {
    transaction.abort();
  } catch {
    // The transaction may already be inactive.
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
